import { enclaveSpecifications } from "@decentriq/core";
import { attestation as attestationProto } from "@decentriq/proto";
import * as forge from "node-forge";
import { v4 as uuidV4 } from "uuid";
import { type ApiCoreStore } from "contexts/apicore/apicore";

export class Store implements ApiCoreStore {
  storage: Map<string, unknown>;

  constructor() {
    this.storage = new Map();
  }

  reset(): void {
    this.storage.clear();
  }

  get<T = unknown>(key: string): T | undefined {
    return this.storage.get(key) as T | undefined;
  }

  pop<T = unknown>(key: string): T | undefined {
    const value = this.storage.get(key);
    this.storage.delete(key);
    return value as T | undefined;
  }

  popStrict<T = unknown>(key: string): T {
    const value = this.pop(key);
    if (value === undefined) {
      throw new Error(`No value corresponding to key '${key}'`);
    }
    return value as T;
  }

  push(value: unknown): string {
    const id = uuidV4();
    this.storage.set(id, value);
    return id;
  }
}

export const getDriverDcapMrsignerAttestationSpecification = (
  configurationEnvironment: string,
  configurationInsecureEnclavesEnabled: boolean
): attestationProto.AttestationSpecification => {
  if (configurationEnvironment === "local") {
    const dcapRootCaDer = forge.util.binary.base64.decode(
      "MIIB/zCCAaagAwIBAgIBADAKBggqhkjOPQQDAjBlMQswCQYDVQQGEwJVUzEaMBgGA1UECgwRSW50ZWwgQ29ycG9yYXRpb24xFDASBgNVBAcMC1NhbnRhIENsYXJhMQswCQYDVQQIDAJDQTEXMBUGA1UEAwwOTW9jayBEQ0FQIFJvb3QwIBcNNzAwMTAxMDAwMDAwWhgPMjA3MDAxMDEwMDAwMDBaMGUxCzAJBgNVBAYTAlVTMRowGAYDVQQKDBFJbnRlbCBDb3Jwb3JhdGlvbjEUMBIGA1UEBwwLU2FudGEgQ2xhcmExCzAJBgNVBAgMAkNBMRcwFQYDVQQDDA5Nb2NrIERDQVAgUm9vdDBZMBMGByqGSM49AgEGCCqGSM49AwEHA0IABCku6eUyEl2rakObDOm9an10KkOHwU/pzh42B/y9d6/qzRhVAPgiCznsolBdwZviDzOhfaCsTxhvAgJ/L6A/+f2jRTBDMBIGA1UdEwEB/wQIMAYBAf8CAQEwHQYDVR0OBBYEFNo5o+5ea0sNMlW/75VgGJCv2AcJMA4GA1UdDwEB/wQEAwIBBjAKBggqhkjOPQQDAgNHADBEAiAHwvykevXFPutkpejfVgO/UGMZlZnpotgMqvRV6BR8hgIgXCZPi9lgj5teIkRgP3PGZ5Oo6wpOp9acROqzkZ6tQGU="
    );
    const mrsigner = forge.util.binary.hex.decode(
      "9affcfae47b848ec2caf1c49b4b283531e1cc425f93582b36806e52a43d78d1a"
    );
    const sigstoreRootOlpcJson = forge.util.binary.base64.decode(
      "eyJzaWduYXR1cmVzIjpbeyJrZXlpZCI6IjE5NDk2ZTE5NzI2NDQ5YzY0MDE5ZTMyNTZiYTg4MzZhMjIxNmUyNWE4Njg4MTc0NmRkZTgyYTgzMDkwMDc0NjMiLCJzaWciOiIxMDkxMGVmN2Q1MjMwNjM2NGMyMTdiYTliODc2OWI0MDhlMWIzNWEyZGFiZWI3ZjBjMzg4NmQyZmU2YTg2ZTZiMDM5OWU5YjFhNjQwMGU4NWU1MTVkODhkYTNjOGNhMTg4N2FlNDFjMTZmNDgyMjJiNWMyNzRkODFhMDhlYTYwMyJ9XSwic2lnbmVkIjp7Il90eXBlIjoicm9vdCIsImNvbnNpc3RlbnRfc25hcHNob3QiOnRydWUsImV4cGlyZXMiOiIyMDIzLTA0LTE4VDE4OjEzOjQzWiIsImtleXMiOnsiMTk0OTZlMTk3MjY0NDljNjQwMTllMzI1NmJhODgzNmEyMjE2ZTI1YTg2ODgxNzQ2ZGRlODJhODMwOTAwNzQ2MyI6eyJrZXl0eXBlIjoiZWQyNTUxOSIsImtleXZhbCI6eyJwdWJsaWMiOiIzMDJhMzAwNTA2MDMyYjY1NzAwMzIxMDAzYjZhMjdiY2NlYjZhNDJkNjJhM2E4ZDAyYTZmMGQ3MzY1MzIxNTc3MWRlMjQzYTYzYWMwNDhhMThiNTlkYTI5In0sInNjaGVtZSI6ImVkMjU1MTkifX0sInJvbGVzIjp7InJvb3QiOnsia2V5aWRzIjpbIjE5NDk2ZTE5NzI2NDQ5YzY0MDE5ZTMyNTZiYTg4MzZhMjIxNmUyNWE4Njg4MTc0NmRkZTgyYTgzMDkwMDc0NjMiXSwidGhyZXNob2xkIjoxfSwic25hcHNob3QiOnsia2V5aWRzIjpbIjQ1YjI4MzgyNWViMTg0Y2FiZDU4MmViMTdiNzRmYzhlZDQwNGY2OGNmNDUyYWNhYmRhZDJlZDZmOTBjZTIxNmIiXSwidGhyZXNob2xkIjoxfSwidGFyZ2V0cyI6eyJrZXlpZHMiOlsiMTk0OTZlMTk3MjY0NDljNjQwMTllMzI1NmJhODgzNmEyMjE2ZTI1YTg2ODgxNzQ2ZGRlODJhODMwOTAwNzQ2MyJdLCJ0aHJlc2hvbGQiOjF9LCJ0aW1lc3RhbXAiOnsia2V5aWRzIjpbImUxODYzYmEwMjA3MDMyMmViYzYyNmRjZWNmOWQ4ODFhM2EzOGMzNWMzYjQxYTgzNzY1YjZhZDZjMzdlYWVjMmEiXSwidGhyZXNob2xkIjoxfX0sInNwZWNfdmVyc2lvbiI6IjEuMCIsInZlcnNpb24iOjV9fQ=="
    );

    return attestationProto.AttestationSpecification.create({
      intelDcapMrsigner: {
        acceptDebug: true,
        dcapRootCaDer,
        isvprodid: 2,
        mrsigner,
        sigstoreRootOlpcJson,
      },
    });
  } else if (configurationInsecureEnclavesEnabled) {
    const dcapRootCaDer = forge.util.binary.base64.decode(
      "MIICjzCCAjSgAwIBAgIUImUM1lqdNInzg7SVUr9QGzknBqwwCgYIKoZIzj0EAwIwaDEaMBgGA1UEAwwRSW50ZWwgU0dYIFJvb3QgQ0ExGjAYBgNVBAoMEUludGVsIENvcnBvcmF0aW9uMRQwEgYDVQQHDAtTYW50YSBDbGFyYTELMAkGA1UECAwCQ0ExCzAJBgNVBAYTAlVTMB4XDTE4MDUyMTEwNDUxMFoXDTQ5MTIzMTIzNTk1OVowaDEaMBgGA1UEAwwRSW50ZWwgU0dYIFJvb3QgQ0ExGjAYBgNVBAoMEUludGVsIENvcnBvcmF0aW9uMRQwEgYDVQQHDAtTYW50YSBDbGFyYTELMAkGA1UECAwCQ0ExCzAJBgNVBAYTAlVTMFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAEC6nEwMDIYZOj/iPWsCzaEKi71OiOSLRFhWGjbnBVJfVnkY4u3IjkDYYL0MxO4mqsyYjlBalTVYxFP2sJBK5zlKOBuzCBuDAfBgNVHSMEGDAWgBQiZQzWWp00ifODtJVSv1AbOScGrDBSBgNVHR8ESzBJMEegRaBDhkFodHRwczovL2NlcnRpZmljYXRlcy50cnVzdGVkc2VydmljZXMuaW50ZWwuY29tL0ludGVsU0dYUm9vdENBLmRlcjAdBgNVHQ4EFgQUImUM1lqdNInzg7SVUr9QGzknBqwwDgYDVR0PAQH/BAQDAgEGMBIGA1UdEwEB/wQIMAYBAf8CAQEwCgYIKoZIzj0EAwIDSQAwRgIhAOW/5QkR+S9CiSDcNoowLuPRLsWGf/Yi7GSX94BgwTwgAiEA4J0lrHoMs+Xo5o/sX6O9QWxHRAvZUGOdRQ7cvqRXaqI="
    );
    const mrsigner = forge.util.binary.hex.decode(
      "9affcfae47b848ec2caf1c49b4b283531e1cc425f93582b36806e52a43d78d1a"
    );
    const sigstoreRootOlpcJson = forge.util.binary.base64.decode(
      "eyJzaWduYXR1cmVzIjpbeyJrZXlpZCI6IjE5NDk2ZTE5NzI2NDQ5YzY0MDE5ZTMyNTZiYTg4MzZhMjIxNmUyNWE4Njg4MTc0NmRkZTgyYTgzMDkwMDc0NjMiLCJzaWciOiIxMDkxMGVmN2Q1MjMwNjM2NGMyMTdiYTliODc2OWI0MDhlMWIzNWEyZGFiZWI3ZjBjMzg4NmQyZmU2YTg2ZTZiMDM5OWU5YjFhNjQwMGU4NWU1MTVkODhkYTNjOGNhMTg4N2FlNDFjMTZmNDgyMjJiNWMyNzRkODFhMDhlYTYwMyJ9XSwic2lnbmVkIjp7Il90eXBlIjoicm9vdCIsImNvbnNpc3RlbnRfc25hcHNob3QiOnRydWUsImV4cGlyZXMiOiIyMDIzLTA0LTE4VDE4OjEzOjQzWiIsImtleXMiOnsiMTk0OTZlMTk3MjY0NDljNjQwMTllMzI1NmJhODgzNmEyMjE2ZTI1YTg2ODgxNzQ2ZGRlODJhODMwOTAwNzQ2MyI6eyJrZXl0eXBlIjoiZWQyNTUxOSIsImtleXZhbCI6eyJwdWJsaWMiOiIzMDJhMzAwNTA2MDMyYjY1NzAwMzIxMDAzYjZhMjdiY2NlYjZhNDJkNjJhM2E4ZDAyYTZmMGQ3MzY1MzIxNTc3MWRlMjQzYTYzYWMwNDhhMThiNTlkYTI5In0sInNjaGVtZSI6ImVkMjU1MTkifX0sInJvbGVzIjp7InJvb3QiOnsia2V5aWRzIjpbIjE5NDk2ZTE5NzI2NDQ5YzY0MDE5ZTMyNTZiYTg4MzZhMjIxNmUyNWE4Njg4MTc0NmRkZTgyYTgzMDkwMDc0NjMiXSwidGhyZXNob2xkIjoxfSwic25hcHNob3QiOnsia2V5aWRzIjpbIjQ1YjI4MzgyNWViMTg0Y2FiZDU4MmViMTdiNzRmYzhlZDQwNGY2OGNmNDUyYWNhYmRhZDJlZDZmOTBjZTIxNmIiXSwidGhyZXNob2xkIjoxfSwidGFyZ2V0cyI6eyJrZXlpZHMiOlsiMTk0OTZlMTk3MjY0NDljNjQwMTllMzI1NmJhODgzNmEyMjE2ZTI1YTg2ODgxNzQ2ZGRlODJhODMwOTAwNzQ2MyJdLCJ0aHJlc2hvbGQiOjF9LCJ0aW1lc3RhbXAiOnsia2V5aWRzIjpbImUxODYzYmEwMjA3MDMyMmViYzYyNmRjZWNmOWQ4ODFhM2EzOGMzNWMzYjQxYTgzNzY1YjZhZDZjMzdlYWVjMmEiXSwidGhyZXNob2xkIjoxfX0sInNwZWNfdmVyc2lvbiI6IjEuMCIsInZlcnNpb24iOjV9fQ=="
    );
    return attestationProto.AttestationSpecification.create({
      intelDcapMrsigner: {
        acceptDebug: true,
        dcapRootCaDer,
        isvprodid: 2,
        mrsigner,
        sigstoreRootOlpcJson,
      },
    });
  } else {
    const enclaveSpecification = enclaveSpecifications.specifications.get(
      "decentriq.driver:mrsigner"
    );
    if (!enclaveSpecification) {
      throw new Error(
        "decentriq.driver:mrsigner not found in enclave specifications"
      );
    }
    return enclaveSpecification.proto;
  }
};
